import { Box, Text, useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { rgba } from 'polished';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mq } from 'styles/breakpoints';
import { rem } from 'utils/fonts';
import useBreakpoint from 'hooks/useBreakpoint';
import { isCorporateThemeSelector } from 'modules/app/appSlice';
import { useSelector } from 'react-redux';
import ProjectTile from './ProjectTile';

//region styled

const Wrapper = styled(Box)`
  margin-top: 30px;
  position: relative;

  ${mq.lg} {
    margin-top: 80px;
    width: 100%;
  }
`;

const ProjectListTitle = styled(Text)`
  font-weight: 600;
  margin-bottom: 30px;
  font-size: ${rem(20)};

  ${mq.lg} {
    margin-bottom: 30px;
  }
`;

const GradientOverlay = styled(Box)<{ isOnTheBottom?: boolean }>`
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 2;
  height: 80px;
  opacity: ${(props): number => (props.isOnTheBottom ? 0 : 0.9)};
  pointer-events: none;
  transition: all 0.1s linear;
`;

const List = styled(Box)`
  list-style: none;
  margin: 0 -10px;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  min-height: 84px;
  scrollbar-width: thin;
  height: 312px;

  ${mq.lg} {
    overflow-y: auto;
  }

  ${mq.xl} {
    height: 336px;
  }

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 11px;
  }
`;

//endregion

interface Project {
  slug: string;
  status: string;
  title: string;
  country: string;
  city: string;
  imageUrl: string;
}

interface Props {
  projects: Project[];
  portfolioTheme: string;
}

const ProjectPortfolio: FC<Props> = ({ projects, portfolioTheme }) => {
  const { colors } = useTheme();
  const { t } = useTranslation('common');

  const [onTheBottom, setOnTheBottom] = useState<boolean>(false);
  const { isSmallDesktopAndAbove } = useBreakpoint();
  const ref = useRef<HTMLDivElement>(null);
  const isCorporateTheme = useSelector(isCorporateThemeSelector);

  function disableWhiteOverlayForNotScrollingElements() {
    if (ref.current && ref.current?.scrollHeight <= ref.current.clientHeight) {
      setOnTheBottom(true);
    }
  }

  useEffect(() => {
    disableWhiteOverlayForNotScrollingElements();
    setTimeout(disableWhiteOverlayForNotScrollingElements, 2000);
  }, []);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLDivElement;

    if (!projects || projects.length < 3) return;

    if (target.scrollHeight - target.scrollTop - 5 <= target.clientHeight) {
      setOnTheBottom(true);
    } else {
      setOnTheBottom(false);
    }
  };

  const areMoreThanTwoProjects = !!projects && projects.length > 2;

  return (
    <Wrapper>
      {isCorporateTheme ? (
        <Text textStyle="h6" fontSize="md">{`${portfolioTheme} ${t('portfolio.projects').toLowerCase()}:`}</Text>
      ) : (
        <ProjectListTitle>{`${portfolioTheme} ${t('portfolio.projects').toLowerCase()}:`}</ProjectListTitle>
      )}
      <List
        as="ul"
        onScroll={handleScroll}
        p={isCorporateTheme ? '24px 10px 0' : '40px 10px 0'}
        ref={ref}
        sx={{
          ...(isCorporateTheme ? {} : { borderTop: `1px solid ${colors.secondary[100]}` }),
          scrollbarColor: `${colors.secondary[100]} transparent`,
          '&::-webkit-scrollbar-thumb': { bg: colors.secondary[100] },
          [mq.lg]: {
            pt: isCorporateTheme ? '30px' : '40px',
          },
        }}
      >
        {projects.map((project: Project) => (
          <ProjectTile key={project.slug} project={project} />
        ))}
      </List>
      {areMoreThanTwoProjects && isSmallDesktopAndAbove && (
        <GradientOverlay isOnTheBottom={onTheBottom} bg={`linear-gradient(180deg, ${rgba(colors.white, 0)} 0%, ${colors.white} 100%)`} />
      )}
    </Wrapper>
  );
};

export default ProjectPortfolio;
